import { NetworkStatus } from "@apollo/client";
import { motion, type PanInfo, Reorder, useDragControls } from "framer-motion";
import _ from "lodash";
import { useOverlayScrollbars } from "overlayscrollbars-react";
import React, {
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  EllipsisIcon,
  GripVerticalIcon,
  PlusIcon,
  Trash2Icon,
  UploadIcon,
  XIcon,
} from "lucide-react";

import {
  FieldDefinitionFieldType,
  FieldDefinitionTableType,
  FieldImperativeHandle,
  TableColumnType,
} from "@/components/@types";
import { Icon } from "@/components/atoms/Icon";
import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { Popper } from "@/components/atoms/Popper";
import { CSV } from "@/components/organisms/CSV";
import { FieldCell } from "@/components/organisms/Field/FieldCell";
import { useResolvedGridAppearance } from "@/components/organisms/Table/Table.tools";
import { List } from "@/components/organisms/List";
import { Values } from "@/constants/values";
import {
  CellProps,
  HotkeyScope,
  getContextClassnameForGrid,
  useContextCellNavigation,
  useContextScope,
} from "@/hooks/useContextTree";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/components/ui/utils";

// =============================================================================
// TYPES
// =============================================================================

type EditMode = "manual" | "csv";

// =============================================================================
// FIELD TYPE HELPERS
// =============================================================================

type FieldCategory = "text" | "numeric" | "reference";

const NUMERIC_TYPES = new Set(["number", "currency", "percent"]);
const REFERENCE_TYPES = new Set(["reference", "references", "tag", "tags"]);

function getFieldCategory(type: string): FieldCategory {
  if (NUMERIC_TYPES.has(type)) return "numeric";
  if (REFERENCE_TYPES.has(type)) return "reference";
  return "text";
}

function getTableFieldSlotProps({
  col,
  rowHeight,
  navCellProps,
  isEditable,
}: {
  col: TableColumnType;
  rowHeight: number;
  navCellProps: Record<string, unknown>;
  isEditable: boolean;
}) {
  const category = getFieldCategory(col.type);
  const isRightAligned = col.style?.textAlign === "right";

  return {
    cell: {
      ...navCellProps,
      // Its alignment the cell's own (focus lifts it to the top while it
      // grows), its overflow a class (focus opens it).
      className: cn(
        navCellProps.className as string | undefined,
        category === "text" && "overflow-hidden",
      ),
      style: {
        display: "flex",
        cursor: isEditable ? "pointer" : "default",
        padding: "6px 8px",
        whiteSpace: "nowrap",
        minWidth: 0,
        width: "100%",
        ...(category === "text" && { textOverflow: "ellipsis" }),
        ...(isRightAligned && { justifyContent: "flex-end" }),
      },
      radius: "0px",
      height: `${rowHeight}px`,
    },
    content: {
      style: {
        minWidth: 0,
        // Text right-aligns itself; a chip or an add button has to be pushed,
        // and the content layer fills the cell.
        ...(isRightAligned &&
          category === "reference" && {
            display: "flex",
            justifyContent: "flex-end",
          }),
        ...(category === "text" && {
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }),
        ...(category === "reference" && {
          width: "100%",
          display: "flex",
          maxWidth: "100%",
          overflow: "hidden",
        }),
        ...(isRightAligned && {
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
        }),
      },
    },
  };
}

function getOrderedColumns(columns: TableColumnType[]): {
  col: TableColumnType;
  originalIndex: number;
  category: FieldCategory;
}[] {
  const lockedColumns: {
    col: TableColumnType;
    originalIndex: number;
    category: FieldCategory;
  }[] = [];

  const textColumns: {
    col: TableColumnType;
    originalIndex: number;
    category: FieldCategory;
  }[] = [];
  const numericColumns: {
    col: TableColumnType;
    originalIndex: number;
    category: FieldCategory;
  }[] = [];
  const referenceColumns: {
    col: TableColumnType;
    originalIndex: number;
    category: FieldCategory;
  }[] = [];

  columns.forEach((col, index) => {
    const category = getFieldCategory(col.type);
    const item = { col, originalIndex: index, category };
    // A locked column stays put while the rest scroll under it, and the sticky
    // offsets only add up while the locked ones lead, so they keep the order
    // they were declared in ahead of the grouping below.
    if (col.isLockedLeft) lockedColumns.push(item);
    else if (category === "text") textColumns.push(item);
    else if (category === "numeric") numericColumns.push(item);
    else referenceColumns.push(item);
  });

  // Journal-style tables define description and references first, amounts last.
  return [
    ...lockedColumns,
    ...textColumns,
    ...referenceColumns,
    ...numericColumns,
  ];
}

// =============================================================================
// TABLE HEADER - Column labels displayed once above all items
// =============================================================================

interface TableHeaderProps {
  columns: TableColumnType[];
  orderedColumns: {
    col: TableColumnType;
    originalIndex: number;
    category: FieldCategory;
  }[];
  showActionsColumn: boolean;
  actionsMenu?: React.ReactNode;
  isEditable?: boolean;
  /** Rows are numbered in the drag handle's column, so it is drawn read-only too. */
  isRowNumbered?: boolean;
  getClassName: (objectKey: string) => string;
  /** Starts dragging a column's edge; what it returns follows the pointer. */
  resizeColumn: (objectKey: string) => (offset: number) => void;
}

// Fixed width for drag handle and actions columns
const DRAG_HANDLE_WIDTH = 36;
const ACTIONS_COLUMN_WIDTH = 36;
const RESIZED_COLUMN_MIN_WIDTH = 48;
const DEFAULT_FIELD_TABLE_ROW_HEIGHT = 34;
const MAX_VISIBLE_ROWS = 10;

const OVERLAY_SCROLLBAR_OPTIONS = {
  defer: true,
  options: {
    scrollbars: {
      visibility: "auto" as const,
      autoHide: "scroll" as const,
      autoHideDelay: 400,
    },
  },
};

function useFieldTableOverlayScrollbars(
  enabled: boolean,
  onScroll?: (scrolled: HTMLElement) => void,
) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [initializeScrollbars] = useOverlayScrollbars(
    OVERLAY_SCROLLBAR_OPTIONS,
  );

  useLayoutEffect(() => {
    if (!enabled || !scrollRef.current) {
      return;
    }
    initializeScrollbars(scrollRef.current);
  }, [enabled, initializeScrollbars]);

  // Whether columns are scrolled under the locked ones, for their edge's
  // shadow, and how far down the rows are, for *onScroll*. Overlay scrollbars
  // scroll a viewport inside the element, so the scroll is caught on its way
  // down - a scroll handler on the element itself never hears it; a field's
  // own input scrolling is not it. Every render: the element comes and goes
  // with the table's empty state.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const mark = (event: Event) => {
      const target = event.target as HTMLElement;
      if (
        target !== el &&
        !target.hasAttribute?.("data-overlayscrollbars-viewport")
      ) {
        return;
      }
      el.toggleAttribute("data-scrolled-x", target.scrollLeft > 0);
      onScroll?.(target);
    };
    el.addEventListener("scroll", mark, { capture: true, passive: true });
    return () => el.removeEventListener("scroll", mark, { capture: true });
  });

  return { scrollRef };
}

type FieldTableLayoutContextValue = {
  rowHeight: number;
  cellSize: "sm" | "md";
  /**
   * Each column's width in pixels once one has been dragged: the one dragged
   * where it was let go, the rest as they were drawn when the first drag began
   * - so a panel narrowed or widened leaves them be.
   */
  widths: Record<string, number>;
  /**
   * The one column that takes the room the others leave - the widest that
   * holds text - until it is dragged itself. None in a table of shares, which
   * fill it between them until one is dragged.
   */
  flexKey: string | null;
  /**
   * The table is as wide as what it sits in, its columns filling it; else as
   * wide as its columns, the rest of the room left empty or scrolled across.
   */
  fills: boolean;
};

const FieldTableLayoutContext =
  React.createContext<FieldTableLayoutContextValue>({
    rowHeight: DEFAULT_FIELD_TABLE_ROW_HEIGHT,
    cellSize: "sm",
    widths: {},
    flexKey: null,
    fills: true,
  });

function useFieldTableLayout() {
  return React.useContext(FieldTableLayoutContext);
}

/*
 * A column locked to the left is held there (sticky) on the cells' ground,
 * parted from what scrolls under it by an inset box-shadow - which survives
 * sticky and border-collapse, where a border often does not - and the last
 * locked column's edge casts a shadow over the columns scrolled under it: only
 * while some are, as the scroll area marks itself (``data-scrolled-x``),
 * fading in and out. A heading's text is ``--field-table-header-color``, or
 * the cell's, in the body type of the table's size at normal weight (not the
 * browser's bold for a ``th``); its padding and line are ToolCallCardTable's.
 */

// Colgroup component for consistent column widths between header and body tables
const TableColgroup = memo(function TableColgroup({
  orderedColumns,
  isEditable,
  isRowNumbered = false,
  showActionsColumn,
  getClassName,
}: {
  orderedColumns: {
    col: TableColumnType;
    originalIndex: number;
    category: FieldCategory;
  }[];
  isEditable: boolean;
  isRowNumbered?: boolean;
  showActionsColumn: boolean;
  getClassName: (objectKey: string) => string;
}) {
  const { widths, flexKey } = useFieldTableLayout();
  // A column of shares is its share of what the shares are of.
  const shares = orderedColumns.reduce(
    (sum, { col }) =>
      sum + (col.widthMode === "percentage" ? (col.width ?? 0) : 0),
    0,
  );
  // Each column at the width it was dragged to, or else asked for - but the
  // one that fills, which takes the width the others leave.
  const widthOf = (col: TableColumnType, category: FieldCategory) =>
    col.objectKey === flexKey
      ? "100%"
      : widths[col.objectKey] != null
        ? widths[col.objectKey]
        : col.widthMode === "percentage" && shares > 0
          ? `${((col.width ?? 0) / shares) * 100}%`
          : getColumnPixelWidth(col, category, widths);

  return (
    <colgroup>
      {(isEditable || isRowNumbered) && (
        <col
          style={{ width: DRAG_HANDLE_WIDTH, minWidth: DRAG_HANDLE_WIDTH }}
        />
      )}
      {orderedColumns.map(({ col, category }) => (
        <col
          key={`col-${col.objectKey}`}
          className={getClassName(col.objectKey)}
          style={{ width: widthOf(col, category) }}
        />
      ))}
      {showActionsColumn && (
        <col
          style={{
            width: ACTIONS_COLUMN_WIDTH,
            minWidth: ACTIONS_COLUMN_WIDTH,
          }}
        />
      )}
    </colgroup>
  );
});

function getLockedColumnLeftOffset(
  orderedColumns: { col: TableColumnType }[],
  colIdx: number,
): number {
  let offset = 0;
  for (let i = 0; i < colIdx; i++) {
    const col = orderedColumns[i].col;
    if (!col.isLockedLeft) continue;
    const category = getFieldCategory(col.type);
    offset += col.width ?? col.minWidth ?? (category === "numeric" ? 100 : 150);
  }
  return offset;
}

function isLastLockedColumn(
  orderedColumns: { col: TableColumnType }[],
  colIdx: number,
): boolean {
  const col = orderedColumns[colIdx]?.col;
  if (!col?.isLockedLeft) return false;
  const next = orderedColumns[colIdx + 1]?.col;
  return !next || !next.isLockedLeft;
}

const TableHeader = memo(function TableHeader({
  orderedColumns,
  showActionsColumn,
  actionsMenu,
  isEditable,
  isRowNumbered = false,
  getClassName,
  resizeColumn,
  tableMinWidth,
}: TableHeaderProps & { tableMinWidth: number }) {
  // The column whose edge is under the pointer, and the one being dragged: its
  // grab stays lit, and the page keeps the resize cursor, after the pointer
  // has left the grab.
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [resizingKey, setResizingKey] = useState<string | null>(null);
  const followResize = useRef<((offset: number) => void) | null>(null);
  // The grab on the divider after *objectKey*: the list table's pill, its
  // middle on the divider. A table cell is hit wherever it is, whatever is
  // drawn over it, so the pill is two halves - one in each heading the divider
  // parts - and either one drags that column.
  const grab = (objectKey: string, half: "start" | "end" | "whole") => (
    <motion.div
      key={`grab-${half}`}
      data-resize={objectKey}
      data-lit={
        hoverKey === objectKey || resizingKey === objectKey || undefined
      }
      onMouseEnter={() => setHoverKey(objectKey)}
      onMouseLeave={() => setHoverKey(null)}
      // Not to select the headings' text on the way.
      onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
      onPanStart={() => {
        setResizingKey(objectKey);
        document.body.style.cursor = "col-resize";
        followResize.current = resizeColumn(objectKey);
      }}
      onPan={(_event: PointerEvent, info: PanInfo) =>
        followResize.current?.(info.offset.x)
      }
      onPanEnd={(_event: PointerEvent, info: PanInfo) => {
        followResize.current?.(info.offset.x);
        followResize.current = null;
        setResizingKey(null);
        document.body.style.cursor = "";
      }}
      // Each half reaches over its own side of the divider, so the pill is
      // drawn on top of it rather than parted by it.
      className={cn(
        "absolute top-[50%] transform-[translateY(-50%)] h-[min(32px,calc(100%-8px))] cursor-col-resize [transition:background-color_0.3s] data-lit:[background-color:var(--primary)]",
        half === "start"
          ? "[left:-0.5px] w-0.5 rounded-[0_2px_2px_0]"
          : half === "end"
            ? "[right:-0.5px] w-0.5 rounded-[2px_0_0_2px]"
            : "right-0 w-1 rounded-[2px]",
      )}
    />
  );
  const { rowHeight, cellSize, fills } = useFieldTableLayout();

  return (
    <table
      className={cn(
        fills ? "w-full" : "w-(--table-min-width)",
        "min-w-(--table-min-width) border-collapse table-fixed",
      )}
      style={
        {
          "--table-min-width":
            tableMinWidth <= 1 && tableMinWidth !== 0
              ? `${tableMinWidth * 100}%`
              : `${tableMinWidth}px`,
        } as CSSProperties
      }
    >
      <TableColgroup
        orderedColumns={orderedColumns}
        isEditable={isEditable ?? false}
        isRowNumbered={isRowNumbered}
        showActionsColumn={showActionsColumn}
        getClassName={getClassName}
      />
      <thead>
        <tr>
          {/* Spacer to match drag handle width */}
          {(isEditable || isRowNumbered) && (
            <th
              className={cn(
                "p-[6px_8px] [border-bottom:1px_solid_var(--border)]",
                cellSize === "md"
                  ? "font-medium text-sm leading-snug text-(--field-table-header-color,inherit) h-(--row-height-2) max-h-(--row-height-2) box-border align-middle"
                  : "font-medium text-xs leading-snug text-(--field-table-header-color,inherit) h-(--row-height-2) max-h-(--row-height-2) box-border align-middle",
                "sticky top-0 z-5 bg-(--field-table-cell-bg) left-0 [box-shadow:inset_-1px_0_0_0_var(--border)] w-9 min-w-9 max-w-9 p-0",
              )}
              style={
                {
                  "--row-height-2":
                    rowHeight <= 1 && rowHeight !== 0
                      ? `${rowHeight * 100}%`
                      : `${rowHeight}px`,
                } as CSSProperties
              }
            />
          )}
          {orderedColumns.map(({ col }, colIdx) => {
            const isLastUnlocked = colIdx === orderedColumns.length - 1;
            const isLocked = col.isLockedLeft === true;
            const isLastLocked = isLastLockedColumn(orderedColumns, colIdx);
            return (
              <th
                key={`header-${col.objectKey}`}
                className={cn(
                  getClassName(col.objectKey),
                  "p-[6px_8px] [border-bottom:1px_solid_var(--border)]",
                  cellSize === "md"
                    ? "font-medium text-sm leading-snug text-(--field-table-header-color,inherit) h-(--row-height-2) max-h-(--row-height-2) box-border align-middle"
                    : "font-medium text-xs leading-snug text-(--field-table-header-color,inherit) h-(--row-height-2) max-h-(--row-height-2) box-border align-middle",
                  "sticky top-0 z-4 bg-(--field-table-header-bg) [text-align:var(--text-align)]",
                  isLocked &&
                    "sticky left-(--left) z-5 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border)]",
                  isLastLocked &&
                    "[&::after]:[content:''] [&::after]:absolute [&::after]:top-0 [&::after]:bottom-0 [&::after]:left-full [&::after]:w-2 [&::after]:pointer-events-none [&::after]:[background:linear-gradient(to_right,_rgb(0_0_0_/_0.05),_transparent)] [&::after]:opacity-[0] [&::after]:[transition:opacity_0.2s_ease] [[data-scrolled-x]_&::after]:opacity-[1]",
                  isLastUnlocked || isLastLocked
                    ? "[border-right-width:initial] [border-right-style:none] border-r-[initial]"
                    : "[border-right-width:1px] [border-right-style:solid] border-r-border",
                )}
                style={
                  {
                    "--row-height-2":
                      rowHeight <= 1 && rowHeight !== 0
                        ? `${rowHeight * 100}%`
                        : `${rowHeight}px`,
                    "--text-align": col.style?.textAlign ?? "left",
                    "--left": isLocked
                      ? `${getLockedColumnLeftOffset(orderedColumns, colIdx)}px`
                      : undefined,
                  } as CSSProperties
                }
              >
                <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                  {col.label}
                </span>
                {colIdx > 0 &&
                  grab(orderedColumns[colIdx - 1].col.objectKey, "start")}
                {/* Every column's edge drags it, the last's too: what is
                    after it moves along. */}
                {grab(col.objectKey, isLastUnlocked ? "whole" : "end")}
              </th>
            );
          })}
          {/* Actions menu in header */}
          {showActionsColumn && (
            <th
              className={cn(
                "p-[6px_8px] [border-bottom:1px_solid_var(--border)]",
                cellSize === "md"
                  ? "font-medium text-sm leading-snug text-(--field-table-header-color,inherit) h-(--row-height-2) max-h-(--row-height-2) box-border align-middle"
                  : "font-medium text-xs leading-snug text-(--field-table-header-color,inherit) h-(--row-height-2) max-h-(--row-height-2) box-border align-middle",
                "sticky top-0 z-5 bg-(--field-table-cell-bg) right-0 [box-shadow:inset_1px_0_0_0_var(--border)] w-9 min-w-9 max-w-9 p-0 text-center align-middle [border-right:none]",
              )}
              style={
                {
                  "--row-height-2":
                    rowHeight <= 1 && rowHeight !== 0
                      ? `${rowHeight * 100}%`
                      : `${rowHeight}px`,
                } as CSSProperties
              }
            >
              {actionsMenu}
            </th>
          )}
        </tr>
      </thead>
    </table>
  );
});

// =============================================================================
// TABLE SUMMARY ROW - Footer aligned with column layout
// =============================================================================

interface SummaryItemFieldProps {
  col: TableColumnType;
  row: Record<string, unknown>;
  getClassName: (objectKey: string) => string;
  isLastCol: boolean;
  colSpan?: number;
  colIdx: number;
  orderedColumns: { col: TableColumnType }[];
  showActionsColumn: boolean;
  isRowNumbered?: boolean;
}

const SummaryItemField = memo(function SummaryItemField({
  col,
  row,
  getClassName,
  isLastCol,
  colSpan = 1,
  colIdx,
  orderedColumns,
  showActionsColumn,
  isRowNumbered = false,
}: SummaryItemFieldProps) {
  const { rowHeight, cellSize } = useFieldTableLayout();
  const cellValue = row[col.objectKey];
  const useFieldCell =
    cellValue == null ||
    (!React.isValidElement(cellValue) && typeof cellValue !== "function");
  const isLocked = col.isLockedLeft === true;
  const isLastLocked = isLastLockedColumn(orderedColumns, colIdx);

  return (
    <td
      colSpan={colSpan > 1 ? colSpan : undefined}
      className={cn(
        getClassName(col.objectKey),
        "p-0",
        isLocked ? "sticky" : "relative",
        "overflow-visible",
        isLocked ? "z-5" : "z-1",
        isLocked &&
          "sticky left-(--left) z-3 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border)]",
        isLastLocked &&
          "[&::after]:[content:''] [&::after]:absolute [&::after]:top-0 [&::after]:bottom-0 [&::after]:left-full [&::after]:w-2 [&::after]:pointer-events-none [&::after]:[background:linear-gradient(to_right,_rgb(0_0_0_/_0.05),_transparent)] [&::after]:opacity-[0] [&::after]:[transition:opacity_0.2s_ease] [[data-scrolled-x]_&::after]:opacity-[1]",
        isLastCol || isLastLocked
          ? "[border-right-width:initial] [border-right-style:none] border-r-[initial]"
          : "[border-right-width:1px] [border-right-style:solid] border-r-border",
        "bg-(--field-table-cell-bg)",
      )}
      style={
        {
          "--left": isLocked
            ? `${
                getLockedColumnLeftOffset(orderedColumns, colIdx) +
                ((showActionsColumn || isRowNumbered) && isLocked
                  ? DRAG_HANDLE_WIDTH
                  : 0)
              }px`
            : undefined,
        } as CSSProperties
      }
    >
      <div
        className="h-(--height) w-0"
        style={
          {
            "--height":
              Number(`${rowHeight}px`) <= 1
                ? `${Number(`${rowHeight}px`) * 100}%`
                : `${rowHeight}px`,
          } as CSSProperties
        }
      />
      <div className="absolute top-0 left-0 right-0 overflow-visible">
        {useFieldCell ? (
          <FieldCell
            type={col.type}
            getValue={col.getValue}
            getValueOptions={col.getValueOptions}
            data={row}
            isEditable={false}
            isSingleClick={true}
            size={cellSize}
            style={col.style}
            hoverBackgroundColor={null}
            slotProps={getTableFieldSlotProps({
              col,
              rowHeight,
              navCellProps: {},
              isEditable: false,
            })}
          />
        ) : (
          <div
            className={cn(
              "flex items-center cursor-default p-[6px_8px] whitespace-nowrap text-ellipsis min-w-0 w-full rounded-none h-(--height)",
              col.style?.textAlign === "right" && "justify-end",
            )}
            style={
              {
                "--height":
                  Number(`${rowHeight}px`) <= 1
                    ? `${Number(`${rowHeight}px`) * 100}%`
                    : `${rowHeight}px`,
              } as CSSProperties
            }
          >
            {cellValue as React.ReactNode}
          </div>
        )}
      </div>
    </td>
  );
});

interface TableSummaryFooterProps {
  orderedColumns: {
    col: TableColumnType;
    originalIndex: number;
    category: FieldCategory;
  }[];
  getClassName: (objectKey: string) => string;
  showActionsColumn: boolean;
  isRowNumbered?: boolean;
  summaryRow: Record<string, unknown>;
  summaryColSpans?: Record<string, number>;
  borderTop?: boolean;
}

const TableSummaryFooter = memo(function TableSummaryFooter({
  orderedColumns,
  getClassName,
  showActionsColumn,
  isRowNumbered = false,
  summaryRow,
  summaryColSpans,
  borderTop = true,
}: TableSummaryFooterProps) {
  const { rowHeight } = useFieldTableLayout();
  const summaryCells: React.ReactNode[] = [];
  for (let colIdx = 0; colIdx < orderedColumns.length; ) {
    const { col } = orderedColumns[colIdx];
    const span = Math.max(1, summaryColSpans?.[col.objectKey] ?? 1);
    const lastCovered = Math.min(colIdx + span - 1, orderedColumns.length - 1);
    summaryCells.push(
      <SummaryItemField
        key={`summary-${col.objectKey}`}
        col={col}
        row={summaryRow}
        getClassName={getClassName}
        colSpan={span}
        colIdx={colIdx}
        orderedColumns={orderedColumns}
        showActionsColumn={showActionsColumn}
        isRowNumbered={isRowNumbered}
        isLastCol={lastCovered === orderedColumns.length - 1}
      />,
    );
    colIdx += span;
  }

  return (
    <tfoot
      className={cn(
        "sticky bottom-0 z-4",
        borderTop && "[border-top:1px_solid_var(--border)]",
      )}
    >
      <tr>
        {(showActionsColumn || isRowNumbered) && (
          <td
            className="sticky left-0 z-5 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border)] w-9 min-w-9 max-w-9 p-0 h-(--row-height) box-border align-middle"
            style={
              {
                "--row-height":
                  rowHeight <= 1 && rowHeight !== 0
                    ? `${rowHeight * 100}%`
                    : `${rowHeight}px`,
              } as CSSProperties
            }
          />
        )}
        {summaryCells}
        {showActionsColumn && (
          <td
            className="sticky right-0 z-3 bg-(--field-table-cell-bg) [box-shadow:inset_1px_0_0_0_var(--border)] w-9 min-w-9 max-w-9 p-0 h-(--row-height) box-border align-middle"
            style={
              {
                "--row-height":
                  rowHeight <= 1 && rowHeight !== 0
                    ? `${rowHeight * 100}%`
                    : `${rowHeight}px`,
              } as CSSProperties
            }
          />
        )}
      </tr>
    </tfoot>
  );
});

interface TableSummaryFillProps {
  orderedColumns: {
    col: TableColumnType;
    originalIndex: number;
    category: FieldCategory;
  }[];
  getClassName: (objectKey: string) => string;
  isEditable: boolean;
  isRowNumbered?: boolean;
  tableMinWidth: number;
}

const TableSummaryFill = memo(function TableSummaryFill({
  orderedColumns,
  getClassName,
  isEditable,
  isRowNumbered = false,
  tableMinWidth,
}: TableSummaryFillProps) {
  const { fills } = useFieldTableLayout();
  return (
    <table
      className={cn(
        fills ? "w-full" : "w-(--table-min-width)",
        "min-w-(--table-min-width) h-full min-h-6 border-collapse table-fixed",
      )}
      style={
        {
          "--table-min-width":
            tableMinWidth <= 1 && tableMinWidth !== 0
              ? `${tableMinWidth * 100}%`
              : `${tableMinWidth}px`,
        } as CSSProperties
      }
    >
      <TableColgroup
        orderedColumns={orderedColumns}
        isEditable={isEditable}
        isRowNumbered={isRowNumbered}
        showActionsColumn={isEditable}
        getClassName={getClassName}
      />
      <tbody className="h-full">
        <tr className="h-full">
          {(isEditable || isRowNumbered) && (
            <td className="sticky left-0 z-3 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border)] [background-image:repeating-linear-gradient(_-45deg,_transparent,_transparent_5px,_var(--border)_5px,_var(--border)_6px_)] w-9 min-w-9 max-w-9 p-0" />
          )}
          {orderedColumns.map(({ col }, colIdx) => {
            const isLastCol = colIdx === orderedColumns.length - 1;
            return (
              <td
                key={`summary-fill-${col.objectKey}`}
                className={cn(
                  getClassName(col.objectKey),
                  col.isLockedLeft === true &&
                    "sticky left-(--left) z-3 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border)]",
                  isLastLockedColumn(orderedColumns, colIdx) &&
                    "[&::after]:[content:''] [&::after]:absolute [&::after]:top-0 [&::after]:bottom-0 [&::after]:left-full [&::after]:w-2 [&::after]:pointer-events-none [&::after]:[background:linear-gradient(to_right,_rgb(0_0_0_/_0.05),_transparent)] [&::after]:opacity-[0] [&::after]:[transition:opacity_0.2s_ease] [[data-scrolled-x]_&::after]:opacity-[1]",
                  "bg-(--field-table-cell-bg) [background-image:repeating-linear-gradient(_-45deg,_transparent,_transparent_5px,_var(--border)_5px,_var(--border)_6px_)] p-0",
                  isLastCol
                    ? "[border-right-width:initial] [border-right-style:none] border-r-[initial]"
                    : "[border-right-width:1px] [border-right-style:solid] border-r-border",
                )}
                style={
                  {
                    "--left":
                      col.isLockedLeft === true
                        ? `${getLockedColumnLeftOffset(orderedColumns, colIdx)}px`
                        : undefined,
                  } as CSSProperties
                }
              />
            );
          })}
          {isEditable && (
            <td className="sticky right-0 z-3 bg-(--field-table-cell-bg) [box-shadow:inset_1px_0_0_0_var(--border)] [background-image:repeating-linear-gradient(_-45deg,_transparent,_transparent_5px,_var(--border)_5px,_var(--border)_6px_)] w-9 min-w-9 max-w-9 p-0" />
          )}
        </tr>
      </tbody>
    </table>
  );
});

// =============================================================================
// ITEM ROW - Single row displayed inline (Reorderable)
// =============================================================================

interface ItemRowProps {
  row: Record<string, any>;
  rowIndex: number;
  rowId: string;
  orderedColumns: {
    col: TableColumnType;
    originalIndex: number;
    category: FieldCategory;
  }[];
  isEditable: boolean;
  rowMutable: boolean;
  rowDeletable: boolean;
  onCellChange: (rowIndex: number, col: TableColumnType, value: any) => void;
  onDeleteRow: (
    rowIndex: number,
    focusAfter?: { row: number; col: number },
  ) => void;
  getCellProps: (opts: {
    rowIndex: number;
    colIndex: number;
    objectKey?: string;
  }) => CellProps;
  getClassName: (objectKey: string) => string;
  deleteColIndex: number;
  isLastRow: boolean;
  outlineLastDataRow?: boolean;
  /** Number the row where its drag handle would be: rows that keep their order. */
  isRowNumbered?: boolean;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
}

const ItemRow = memo(function ItemRow({
  row,
  rowIndex,
  rowId,
  orderedColumns,
  isEditable,
  rowMutable,
  rowDeletable,
  onCellChange,
  onDeleteRow,
  getCellProps,
  getClassName,
  deleteColIndex,
  isLastRow,
  outlineLastDataRow = false,
  isRowNumbered = false,
  onInteractionStart,
  onInteractionEnd,
}: ItemRowProps) {
  const { rowHeight } = useFieldTableLayout();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusedWithin, setIsFocusedWithin] = useState(false);
  const dragControls = useDragControls();
  const suppressBottomBorder = isLastRow && !outlineLastDataRow;

  const handleRowFocusCapture = useCallback(() => {
    setIsFocusedWithin(true);
  }, []);

  const handleRowBlurCapture = useCallback(
    (event: React.FocusEvent<HTMLTableRowElement>) => {
      const nextTarget = event.relatedTarget as Node | null;
      if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
        setIsFocusedWithin(false);
      }
    },
    [],
  );

  // Get cell props for delete button
  const deleteCellProps = getCellProps({
    rowIndex,
    colIndex: deleteColIndex,
    objectKey: "__delete__",
  });

  // The table's own colour, under every cell: transparent, a cell showed
  // whatever the table sat on while its locked column - opaque, to hide what
  // scrolls under it - showed this.
  const rowBgColor = isHovered
    ? "var(--field-table-row-hover-bg)"
    : "var(--field-table-cell-bg)";
  const showRowChrome = isEditable && rowMutable;
  const showRowChromeSpacer = isEditable && !rowMutable;
  const showDeleteButton = isEditable && rowDeletable;

  return (
    <Reorder.Item
      value={rowId}
      dragListener={false}
      dragControls={dragControls}
      as="tr"
      // Table rows + absolutely positioned cells measure badly under
      // layout projection; the initial pass can leave a translateX that
      // looks like extra first-column indent until a later remeasure
      // (e.g. focus). Keep reorder drag; disable layout animations.
      transition={{ layout: { duration: 0 } }}
      onDragStart={() => onInteractionStart?.()}
      onDragEnd={() => onInteractionEnd?.()}
      onFocusCapture={handleRowFocusCapture}
      onBlurCapture={handleRowBlurCapture}
      style={{
        listStyle: "none",
        backgroundColor: rowBgColor,
        position: isFocusedWithin ? "relative" : undefined,
        zIndex: isFocusedWithin ? 10 : undefined,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Row number, for rows that keep their order */}
      {isRowNumbered ? (
        <td
          data-testid="field-table-row-number"
          className={cn(
            "p-0 sticky left-0 z-3 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border)] w-9 min-w-9 max-w-9",
            isHovered
              ? "bg-(--field-table-row-hover-bg)"
              : "bg-(--field-table-cell-bg)",
            "align-middle text-center text-muted-foreground/80 text-[11px] [font-variant-numeric:tabular-nums]",
            suppressBottomBorder
              ? "[border-bottom-width:initial] [border-bottom-style:none] border-b-[initial]"
              : "[border-bottom-width:1px] [border-bottom-style:solid] border-b-border",
          )}
        >
          {rowIndex + 1}
        </td>
      ) : showRowChrome ? (
        // Drag handle
        <td
          onPointerDown={(e) => dragControls.start(e)}
          // Keep cell bg fully opaque — opacity on the td shows scrolled content through
          className={cn(
            "p-0 sticky left-0 z-3 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border)] w-9 min-w-9 max-w-9",
            isHovered
              ? "bg-(--field-table-row-hover-bg)"
              : "bg-(--field-table-cell-bg)",
            "align-middle text-center cursor-grab text-muted-foreground/80",
            suppressBottomBorder
              ? "[border-bottom-width:initial] [border-bottom-style:none] border-b-[initial]"
              : "[border-bottom-width:1px] [border-bottom-style:solid] border-b-border",
            "hover:text-muted-foreground active:cursor-grabbing",
          )}
        >
          <div
            className={cn(
              "inline-flex",
              isHovered ? "opacity-[0.8]" : "opacity-[0.5]",
              "[transition:opacity_0.15s_ease-in-out] hover:opacity-[1]",
            )}
          >
            <Icon ReactIcon={GripVerticalIcon} size="sm" />
          </div>
        </td>
      ) : showRowChromeSpacer ? (
        <td
          className={cn(
            "p-0 sticky left-0 z-3 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border)] w-9 min-w-9 max-w-9",
            isHovered
              ? "bg-(--field-table-row-hover-bg)"
              : "bg-(--field-table-cell-bg)",
            suppressBottomBorder
              ? "[border-bottom-width:initial] [border-bottom-style:none] border-b-[initial]"
              : "[border-bottom-width:1px] [border-bottom-style:solid] border-b-border",
          )}
        />
      ) : null}

      {/* Data columns */}
      {orderedColumns.map(({ col, originalIndex }, colIdx) => (
        <ItemField
          key={`${col.objectKey}-${originalIndex}`}
          col={col}
          row={row}
          rowIndex={rowIndex}
          colIndex={originalIndex}
          isEditable={isEditable && rowMutable}
          onCellChange={onCellChange}
          getCellProps={getCellProps}
          getClassName={getClassName}
          isLastRow={isLastRow}
          suppressBottomBorder={suppressBottomBorder}
          isLastCol={colIdx === orderedColumns.length - 1}
          isLocked={col.isLockedLeft === true}
          lockedLeft={getLockedColumnLeftOffset(orderedColumns, colIdx)}
          isLastLocked={isLastLockedColumn(orderedColumns, colIdx)}
          isRowHovered={isHovered}
        />
      ))}

      {/* Delete button */}
      {showDeleteButton ? (
        <td
          className={cn(
            "p-0 sticky right-0 z-3 bg-(--field-table-cell-bg) [box-shadow:inset_1px_0_0_0_var(--border)] w-9 min-w-9 max-w-9",
            isHovered
              ? "bg-(--field-table-row-hover-bg)"
              : "bg-(--field-table-cell-bg)",
            "align-middle text-center",
            suppressBottomBorder
              ? "[border-bottom-width:initial] [border-bottom-style:none] border-b-[initial]"
              : "[border-bottom-width:1px] [border-bottom-style:solid] border-b-border",
          )}
        >
          <div
            className="flex items-center justify-center w-full h-full min-h-(--row-height)"
            style={
              {
                "--row-height":
                  rowHeight <= 1 && rowHeight !== 0
                    ? `${rowHeight * 100}%`
                    : `${rowHeight}px`,
              } as CSSProperties
            }
          >
            <Button
              variant="ghost"
              size="icon-xs"
              ref={deleteCellProps.ref}
              data-row-index={deleteCellProps["data-row-index"]}
              data-col-index={deleteCellProps["data-col-index"]}
              onClick={() =>
                onDeleteRow(rowIndex, {
                  row: Math.max(0, rowIndex - 1),
                  col: deleteColIndex,
                })
              }
              className={cn(
                "m-0",
                isHovered ? "opacity-[1]" : "opacity-[0.5]",
                "[transition:opacity_0.15s_ease-in-out] hover:text-destructive focus-visible:opacity-[1] focus-visible:[outline:2px_solid_var(--ring)] focus-visible:outline-offset-2",
              )}
            >
              <Icon ReactIcon={Trash2Icon} size="sm" />
            </Button>
          </div>
        </td>
      ) : isEditable ? (
        <td
          className={cn(
            "p-0 sticky right-0 z-3 bg-(--field-table-cell-bg) [box-shadow:inset_1px_0_0_0_var(--border)] w-9 min-w-9 max-w-9",
            isHovered
              ? "bg-(--field-table-row-hover-bg)"
              : "bg-(--field-table-cell-bg)",
            suppressBottomBorder
              ? "[border-bottom-width:initial] [border-bottom-style:none] border-b-[initial]"
              : "[border-bottom-width:1px] [border-bottom-style:solid] border-b-border",
          )}
        />
      ) : null}
    </Reorder.Item>
  );
});

// =============================================================================
// ITEM FIELD - Single field within a row using FieldCell
// =============================================================================

interface ItemFieldProps {
  col: TableColumnType;
  row: Record<string, any>;
  rowIndex: number;
  colIndex: number;
  isEditable: boolean;
  onCellChange: (rowIndex: number, col: TableColumnType, value: any) => void;
  getCellProps: (opts: {
    rowIndex: number;
    colIndex: number;
    objectKey?: string;
  }) => CellProps;
  getClassName: (objectKey: string) => string;
  isLastRow: boolean;
  suppressBottomBorder?: boolean;
  isLastCol: boolean;
  isLocked?: boolean;
  lockedLeft?: number;
  isLastLocked?: boolean;
  isRowHovered?: boolean;
}

const ItemField = memo(function ItemField({
  col,
  row,
  rowIndex,
  colIndex,
  isEditable,
  onCellChange,
  getCellProps,
  getClassName,
  isLastRow,
  suppressBottomBorder = false,
  isLastCol,
  isLocked = false,
  lockedLeft = 0,
  isLastLocked = false,
  isRowHovered = false,
}: ItemFieldProps) {
  const { rowHeight, cellSize } = useFieldTableLayout();
  // A column opts out of editing with ``isDisabled`` — it shows a value the
  // user cannot set, typically one the API computes (a line's tax amount from
  // its tax rate and amount). Such a cell reads like one in a read-only table:
  // no caret, no edit affordance, and nothing to submit.
  const isCellEditable = isEditable && col.isDisabled !== true;
  const handleChange = useCallback(
    (value: any) => {
      onCellChange(rowIndex, col, value);
    },
    [rowIndex, col, onCellChange],
  );

  const fieldEvents = useMemo(
    () =>
      isCellEditable
        ? {
            onChangeField: handleChange,
            onDeleteField: () => handleChange(null),
            // Interaction lock is owned by FieldTable (focusin/focusout +
            // drag). Do not wire FieldCell begin/end — cell remounts would
            // drop the lock mid-edit.
          }
        : undefined,
    [isCellEditable, handleChange],
  );

  // Get cell props from useContextTree.
  // Exclude className — the <td> already has the column class (getClassName), and
  // spreading it onto the FieldCell outer Box would apply the resize-hook's
  // width rule to the inner element too, shrinking it.
  const { className: _colClass, ...navCellProps } = getCellProps({
    rowIndex,
    colIndex,
    objectKey: col.objectKey,
  });

  // Support multiline for text fields (use Shift+Enter for newlines)
  const isMultiline = col.isMultiline ?? false;

  return (
    <td
      className={cn(
        getClassName(col.objectKey),
        "p-0",
        isLocked ? "sticky" : "relative",
        "overflow-visible",
        isLocked ? "z-2" : "z-1",
        "focus-within:z-10",
        isLocked &&
          (isRowHovered
            ? "sticky left-(--locked-left) z-3 bg-(--field-table-row-hover-bg) [box-shadow:inset_-1px_0_0_0_var(--border)]"
            : "sticky left-(--locked-left) z-3 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border)]"),
        suppressBottomBorder
          ? "[border-bottom-width:initial] [border-bottom-style:none] border-b-[initial]"
          : "[border-bottom-width:1px] [border-bottom-style:solid] border-b-border",
        isLastLocked &&
          "[&::after]:[content:''] [&::after]:absolute [&::after]:top-0 [&::after]:bottom-0 [&::after]:left-full [&::after]:w-2 [&::after]:pointer-events-none [&::after]:[background:linear-gradient(to_right,_rgb(0_0_0_/_0.05),_transparent)] [&::after]:opacity-[0] [&::after]:[transition:opacity_0.2s_ease] [[data-scrolled-x]_&::after]:opacity-[1]",
        isLastCol || isLastLocked
          ? "[border-right-width:initial] [border-right-style:none] border-r-[initial]"
          : "[border-right-width:1px] [border-right-style:solid] border-r-border",
      )}
      style={
        {
          "--locked-left": isLocked ? `${lockedLeft}px` : undefined,
        } as CSSProperties
      }
    >
      <div
        className="h-(--height) w-0"
        style={
          {
            "--height":
              Number(`${rowHeight}px`) <= 1
                ? `${Number(`${rowHeight}px`) * 100}%`
                : `${rowHeight}px`,
          } as CSSProperties
        }
      />
      <div className="absolute top-0 left-0 right-0 overflow-visible z-1 focus-within:z-100">
        <FieldCell
          type={col.type}
          getValue={col.getValue}
          getValueOptions={col.getValueOptions}
          data={row}
          isEditable={isCellEditable}
          fieldEvents={fieldEvents}
          isMultiline={isMultiline}
          isSingleClick={true}
          size={cellSize}
          style={
            // A reference fills its cell so the chip lines up with the column
            // edges — except in a right-aligned column, where filling would
            // leave the cell's ``justify-content`` nothing to push against and
            // the chip would sit on the left of a full-width box.
            getFieldCategory(col.type) === "reference" &&
            col.style?.textAlign !== "right"
              ? { ...col.style, width: "100%" }
              : col.style
          }
          hoverBackgroundColor={null}
          slotProps={getTableFieldSlotProps({
            col,
            rowHeight,
            navCellProps,
            isEditable: isCellEditable,
          })}
        />
      </div>
    </td>
  );
});

// =============================================================================
// ACTIONS MENU
// =============================================================================

interface ActionsMenuProps {
  editMode: EditMode;
  hasRows: boolean;
  selectedCount: number;
  onToggleEditMode: () => void;
  onAddRow: () => void;
  onDeleteSelected: () => void;
}

const ActionsMenu = memo(function ActionsMenu({
  editMode,
  hasRows,
  selectedCount,
  onToggleEditMode,
  onAddRow,
  onDeleteSelected,
}: ActionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  const items = useMemo(() => {
    const result = [];

    result.push({
      id: "csv",
      label:
        editMode === "csv"
          ? "Enter manually"
          : hasRows
            ? "Import CSV"
            : "Upload CSV",
      icon: UploadIcon,
      action: () => {
        onToggleEditMode();
        setIsOpen(false);
      },
    });

    if (editMode === "manual") {
      result.push({
        id: "add",
        label: "Add new line",
        icon: PlusIcon,
        action: () => {
          onAddRow();
          setIsOpen(false);
        },
      });
    }

    if (selectedCount > 0) {
      result.push({
        id: "delete",
        label: `Delete ${selectedCount} item${selectedCount > 1 ? "s" : ""}`,
        icon: Trash2Icon,
        color: "danger",
        action: () => {
          onDeleteSelected();
          setIsOpen(false);
        },
      });
    }

    return result;
  }, [
    editMode,
    hasRows,
    selectedCount,
    onToggleEditMode,
    onAddRow,
    onDeleteSelected,
  ]);

  return (
    <Popper
      anchor={
        <Button variant="ghost" size="icon-xs">
          <Icon ReactIcon={EllipsisIcon} size="sm" />
        </Button>
      }
      isOpenState={[isOpen, setIsOpen]}
      placement="bottom-end"
      mainAxisOffset={4}
      isClickable
      isHoverable={false}
    >
      <List
        data={items}
        onItemClick={(item) => item.action()}
        size="sm"
        className="min-w-40"
      >
        <List.Items>
          {(item: any) => (
            <List.Item
              value={item}
              data-testid={`field-table-action-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div
                className={cn(
                  "flex items-center gap-2",
                  item.color === "danger" ? "text-destructive" : "text-inherit",
                )}
              >
                <Icon ReactIcon={item.icon} size="sm" />
                <div className="font-medium text-xs leading-snug text-foreground">
                  {item.label}
                </div>
              </div>
            </List.Item>
          )}
        </List.Items>
      </List>
    </Popper>
  );
});

// =============================================================================
// CSV UPLOADER
// =============================================================================

interface CsvUploaderProps {
  columns: TableColumnType[];
  onSubmit: (rows: Record<string, any>[]) => void;
  onCancel: () => void;
}

const CsvUploader = memo(function CsvUploader({
  columns,
  onSubmit,
  onCancel,
}: CsvUploaderProps) {
  const handleSubmit = useCallback(
    (
      _file: File,
      _headers: any,
      _headerRow: number,
      uploadedRows: Record<string, any>[],
    ) => {
      const processedRows = uploadedRows.map((row) =>
        columns.reduce(
          (acc, col) => {
            acc[col.objectKey] = row[col.objectKey] ?? "";
            return acc;
          },
          {} as Record<string, any>,
        ),
      );
      onSubmit(processedRows);
    },
    [columns, onSubmit],
  );

  return (
    <div
      // border: `1px solid ${theme.palette.divider}`,
      className="p-3 rounded-md bg-card"
    >
      <div className="flex justify-between items-center mb-3">
        <div className="font-medium text-xs leading-snug text-foreground [font-weight:600]">
          Import from CSV
        </div>
        <Button variant="ghost" size="icon-xs" onClick={onCancel}>
          <Icon ReactIcon={XIcon} size="sm" />
        </Button>
      </div>
      <CSV expectedHeaders={columns} onSubmit={handleSubmit} />
    </div>
  );
});

// =============================================================================
// ADD ITEM ROW - Inline table row with + button to add new rows
// =============================================================================

interface AddItemRowProps {
  onClick: () => void;
  getCellProps: (opts: {
    rowIndex: number;
    colIndex: number;
    objectKey?: string;
  }) => CellProps;
  rowIndex: number;
  orderedColumns: {
    col: TableColumnType;
    originalIndex: number;
    category: FieldCategory;
  }[];
  getClassName: (objectKey: string) => string;
  hasActionsColumn: boolean;
  stickyBottomOffset?: number;
  hasRowsAbove?: boolean;
}

const AddItemRow = memo(function AddItemRow({
  onClick,
  getCellProps,
  rowIndex,
  orderedColumns,
  getClassName,
  hasActionsColumn,
  stickyBottomOffset = 0,
  hasRowsAbove = true,
}: AddItemRowProps) {
  const { rowHeight, cellSize } = useFieldTableLayout();

  // The row draws its own top divider (``hasRowsAbove``) because the last
  // data row above it suppresses its bottom border (see
  // ``suppressBottomBorder``). On an empty table there is no such row: the add
  // row sits straight under the header, whose bottom border is already there,
  // and drawing both stacked them into a 2px rule.

  // Register the add button as a cell at (rowIndex, 0) — left locked column
  const cellProps = getCellProps({
    rowIndex,
    colIndex: 0,
    objectKey: "__add__",
  });

  const labelColumnIndex = orderedColumns.findIndex(
    ({ category }) => category === "text",
  );

  return (
    <tr
      onClick={onClick}
      className="cursor-pointer hover:[&_td]:bg-(--field-table-row-hover-bg) [&:hover]:[&_[data-add-line-icon]]:text-muted-foreground [&:hover]:[&_[data-add-line-label]]:text-muted-foreground/80"
    >
      <td
        ref={cellProps.ref}
        className={cn(
          cellProps.className,
          "sticky bottom-(--sticky-bottom-offset) z-5 bg-(--field-table-cell-bg) [border-bottom:1px_solid_var(--border)] h-(--row-height) box-border left-0 w-9 min-w-9 max-w-9 p-0 text-muted-foreground/80 text-center align-middle",
          hasRowsAbove
            ? "[box-shadow:inset_-1px_0_0_0_var(--border),inset_0_1px_0_0_var(--border)]"
            : "[box-shadow:inset_-1px_0_0_0_var(--border)]",
          "[transition:color_0.15s_ease-in-out] focus:[outline:2px_solid_var(--primary)] [&:focus]:[outline-offset:-2px]",
        )}
        style={
          {
            "--sticky-bottom-offset": `${stickyBottomOffset}px`,
            "--row-height":
              rowHeight <= 1 && rowHeight !== 0
                ? `${rowHeight * 100}%`
                : `${rowHeight}px`,
          } as CSSProperties
        }
        data-row-index={cellProps["data-row-index"]}
        data-col-index={cellProps["data-col-index"]}
        tabIndex={0}
        aria-label="Add line"
        data-testid="field-table-add-row"
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            onClick();
          }
        }}
      >
        <div
          data-add-line-icon
          className="flex items-center justify-center w-full h-full min-h-(--row-height) text-muted-foreground/80"
          style={
            {
              "--row-height":
                rowHeight <= 1 && rowHeight !== 0
                  ? `${rowHeight * 100}%`
                  : `${rowHeight}px`,
            } as CSSProperties
          }
        >
          <Icon ReactIcon={PlusIcon} size="md" />
        </div>
      </td>
      {orderedColumns.map(({ col }, colIdx) => {
        const isLocked = col.isLockedLeft === true;
        const isLastLocked = isLastLockedColumn(orderedColumns, colIdx);
        const isLastCol = colIdx === orderedColumns.length - 1;
        const isRightAligned = col.style?.textAlign === "right";
        const showLabel =
          colIdx === (labelColumnIndex >= 0 ? labelColumnIndex : 0);

        return (
          <td
            key={`add-row-${col.objectKey}`}
            className={cn(
              getClassName(col.objectKey),
              "sticky bottom-(--sticky-bottom-offset-2) z-4 bg-(--field-table-cell-bg) [border-bottom:1px_solid_var(--border)] h-(--row-height-2) box-border p-[6px_8px] align-middle",
              isLocked
                ? hasRowsAbove
                  ? "sticky left-(--left) z-5 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border),inset_0_1px_0_0_var(--border)]"
                  : "sticky left-(--left) z-5 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border)]"
                : hasRowsAbove
                  ? "z-1 [box-shadow:inset_0_1px_0_0_var(--border)]"
                  : "z-1 [box-shadow:none]",
              isLastLocked &&
                "[&::after]:[content:''] [&::after]:absolute [&::after]:top-0 [&::after]:bottom-0 [&::after]:left-full [&::after]:w-2 [&::after]:pointer-events-none [&::after]:[background:linear-gradient(to_right,_rgb(0_0_0_/_0.05),_transparent)] [&::after]:opacity-[0] [&::after]:[transition:opacity_0.2s_ease] [[data-scrolled-x]_&::after]:opacity-[1]",
              isLastCol || isLastLocked
                ? "[border-right-width:initial] [border-right-style:none] border-r-[initial]"
                : "[border-right-width:1px] [border-right-style:solid] border-r-border",
            )}
            style={
              {
                "--sticky-bottom-offset-2": `${stickyBottomOffset}px`,
                "--row-height-2":
                  rowHeight <= 1 && rowHeight !== 0
                    ? `${rowHeight * 100}%`
                    : `${rowHeight}px`,
                "--left": isLocked
                  ? `${getLockedColumnLeftOffset(orderedColumns, colIdx)}px`
                  : undefined,
              } as CSSProperties
            }
          >
            <div
              className={cn(
                "flex items-center w-full min-h-(--min-height)",
                isRightAligned && "justify-end",
              )}
              style={
                {
                  "--min-height":
                    rowHeight - 12 <= 1 && rowHeight - 12 !== 0
                      ? `${(rowHeight - 12) * 100}%`
                      : `${rowHeight - 12}px`,
                } as CSSProperties
              }
            >
              {showLabel ? (
                <div
                  data-add-line-label
                  className={cn(
                    "whitespace-nowrap",
                    cellSize === "md"
                      ? "font-medium text-sm leading-snug text-foreground"
                      : "font-medium text-xs leading-snug text-foreground",
                    "text-muted-foreground/80",
                  )}
                >
                  Add line...
                </div>
              ) : null}
            </div>
          </td>
        );
      })}
      {hasActionsColumn && (
        <td
          className={cn(
            "sticky bottom-(--sticky-bottom-offset) z-5 bg-(--field-table-cell-bg) [border-bottom:1px_solid_var(--border)] h-(--row-height) box-border right-0 [box-shadow:inset_1px_0_0_0_var(--border)] w-9 min-w-9 p-0 text-muted-foreground/80 text-center align-middle",
            hasRowsAbove
              ? "[box-shadow:inset_1px_0_0_0_var(--border),inset_0_1px_0_0_var(--border)]"
              : "[box-shadow:inset_1px_0_0_0_var(--border)]",
          )}
          style={
            {
              "--sticky-bottom-offset": `${stickyBottomOffset}px`,
              "--row-height":
                rowHeight <= 1 && rowHeight !== 0
                  ? `${rowHeight * 100}%`
                  : `${rowHeight}px`,
            } as CSSProperties
          }
        >
          <div
            className="flex items-center justify-center w-full h-full min-h-(--row-height)"
            style={
              {
                "--row-height":
                  rowHeight <= 1 && rowHeight !== 0
                    ? `${rowHeight * 100}%`
                    : `${rowHeight}px`,
              } as CSSProperties
            }
          >
            <HotkeyDecorator hotkey="n" />
          </div>
        </td>
      )}
    </tr>
  );
});

// =============================================================================
// PAGINATION
// =============================================================================

export type FieldTablePaginationProps = {
  loadNext?: () => void;
  totalCount?: number;
  isLoading?: boolean;
  networkStatus?: NetworkStatus;
};

// =============================================================================
// MAIN COMPONENT - FieldTableValue
// =============================================================================

function FieldTableValue<T extends FieldDefinitionTableType<T>>({
  value: propRows,
  valueOptions,
  events,
  pagination,
  showHeaderWhenEmpty,
  summaryRow,
  summaryColSpans,
  summaryOnly = false,
  overlayScrollbars = false,
  columnContextId,
  resizeColumn,
  isRowMutable,
  isRowDeletable,
  isRowNumbered,
  onDeleteRow,
  createRow,
  ref,
  style,
}: FieldDefinitionFieldType<T> & {
  pagination?: FieldTablePaginationProps;
  showHeaderWhenEmpty?: boolean;
  summaryRow?: Record<string, unknown>;
  summaryColSpans?: Record<string, number>;
  summaryOnly?: boolean;
  overlayScrollbars?: boolean;
  columnContextId: string;
  resizeColumn: TableHeaderProps["resizeColumn"];
  isRowMutable?: (row: Record<string, any>, rowIndex: number) => boolean;
  isRowDeletable?: (row: Record<string, any>, rowIndex: number) => boolean;
  isRowNumbered?: boolean;
  onDeleteRow?: (row: Record<string, any>, rowIndex: number) => void;
  createRow?: () => Record<string, any>;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const gridColors = useResolvedGridAppearance();
  const { rowHeight, widths, fills } = useFieldTableLayout();
  const containerRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(false);
  const fillHeight =
    style?.height === "100%" || style?.flex === 1 || style?.flex === "1";
  const showSummaryFill = summaryRow != null && fillHeight;

  // Generate unique row ID
  const generateRowId = useCallback(() => {
    return `row-${Math.random().toString(36).substr(2, 9)}-${Date.now()}`;
  }, []);

  const withRowMetadata = useCallback((row: Record<string, any>) => {
    if (row?.metadata?._frontend_uuid) {
      return row;
    }
    if (row?.identifier) {
      return {
        ...row,
        metadata: {
          ...(row?.metadata ?? {}),
          _frontend_uuid: row.identifier,
        },
      };
    }
    return {
      ...row,
      metadata: {
        ...(row?.metadata ?? {}),
        _frontend_uuid: crypto.randomUUID(),
      },
    };
  }, []);

  // State
  const [localRows, setLocalRows] = useState<Record<string, any>[]>(() =>
    (_.cloneDeep(propRows) ?? []).map(withRowMetadata),
  );
  const [rowIds, setRowIds] = useState<string[]>(() =>
    (propRows ?? []).map(() => generateRowId()),
  );
  const localRowsRef = useRef(localRows);
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
  const [editMode, setEditMode] = useState<EditMode>("manual");

  // Keep rowIds aligned with localRows without reminting existing keys
  // (full remint remounts every cell and drops focus mid-edit).
  useEffect(() => {
    if (rowIds.length < localRows.length) {
      setRowIds((prev) => [
        ...prev,
        ...Array.from({ length: localRows.length - prev.length }, () =>
          generateRowId(),
        ),
      ]);
    } else if (rowIds.length > localRows.length) {
      setRowIds((prev) => prev.slice(0, localRows.length));
    }
  }, [localRows.length, rowIds.length, generateRowId]);

  // Cap height in compact embeds; fill the parent panel when requested via style.
  const needsScroll = fillHeight || localRows.length > MAX_VISIBLE_ROWS;
  const useOverlayScrollbarLayout = overlayScrollbars || needsScroll;
  const shouldLoadMoreRef = useRef(false);

  // A paged table asks for its next page as what scrolls - the overlay
  // scrollbars' viewport, not the scrollport around it - nears its end.
  const handleScroll = useCallback(
    (el: HTMLElement) => {
      if (!pagination?.loadNext) return;

      const nearBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - rowHeight * 5;
      const totalCount = pagination.totalCount ?? localRowsRef.current.length;

      if (
        nearBottom &&
        !shouldLoadMoreRef.current &&
        !pagination.isLoading &&
        pagination.networkStatus !== NetworkStatus.fetchMore &&
        localRowsRef.current.length < totalCount
      ) {
        shouldLoadMoreRef.current = true;
        pagination.loadNext();
      }
    },
    [pagination, rowHeight],
  );
  const { scrollRef } = useFieldTableOverlayScrollbars(
    useOverlayScrollbarLayout,
    handleScroll,
  );

  // Extract from props
  const { onChangeField = () => {}, onSubmitField = () => {} } = events ?? {};
  const columns = useMemo(
    () =>
      (valueOptions as ReturnType<FieldDefinitionTableType["getValueOptions"]>)
        ?.columns ?? [],
    [valueOptions],
  );
  const orderedColumns = useMemo(() => getOrderedColumns(columns), [columns]);
  const isEditable = !!events?.onChangeField;
  const isRowMutableFn = useCallback(
    (row: Record<string, any>, rowIndex: number) =>
      isRowMutable ? isRowMutable(row, rowIndex) : isEditable,
    [isEditable, isRowMutable],
  );
  // Rows can be removable without being editable (e.g. derived rows that stand
  // in for a selection elsewhere).
  const isRowDeletableFn = useCallback(
    (row: Record<string, any>, rowIndex: number) =>
      isRowDeletable
        ? isRowDeletable(row, rowIndex)
        : isRowMutableFn(row, rowIndex),
    [isRowDeletable, isRowMutableFn],
  );
  useEffect(() => {
    if (pagination?.networkStatus !== NetworkStatus.fetchMore) {
      shouldLoadMoreRef.current = false;
    }
  }, [pagination?.networkStatus]);

  const tableMinWidth = useMemo(() => {
    const allPercentageColumns =
      orderedColumns.length > 0 &&
      orderedColumns.every(({ col }) => col.widthMode === "percentage");
    if (allPercentageColumns && Object.keys(widths).length === 0) {
      return 0;
    }

    const dataMins = orderedColumns.reduce((sum, { col, category }) => {
      return sum + getColumnPixelWidth(col, category, widths);
    }, 0);
    const locked =
      (isEditable || isRowNumbered ? DRAG_HANDLE_WIDTH : 0) +
      (isEditable ? ACTIONS_COLUMN_WIDTH : 0);
    return locked + dataMins;
  }, [orderedColumns, isEditable, isRowNumbered, widths]);

  const deleteColIndex = orderedColumns.length;
  const rowCount = localRows.length + (isEditable ? 1 : 0);
  const colCount = columns.length + (isEditable ? 1 : 0);

  // Ref for the delete handler so the hotkey callback can call it without stale closures
  const deleteRowRef = useRef<
    (rowIndex: number, focusAfter?: { row: number; col: number }) => void
  >(() => {});
  const handleAddRowRef = useRef<() => void>(() => {});
  const isRowMutableFnRef = useRef(isRowMutableFn);
  isRowMutableFnRef.current = isRowMutableFn;
  const isRowDeletableFnRef = useRef(isRowDeletableFn);
  isRowDeletableFnRef.current = isRowDeletableFn;
  const onDeleteRowRef = useRef(onDeleteRow);
  onDeleteRowRef.current = onDeleteRow;

  const tableHotkeys = useMemo(
    () =>
      isEditable
        ? [
            {
              id: "add-row",
              combos: { mac: "n", windows: "n", linux: "n" },
              callback: (e: KeyboardEvent) => {
                e.preventDefault();
                handleAddRowRef.current();
              },
              scopeCheck: HotkeyScope.within,
            },
            {
              id: "delete-row",
              combos: {
                mac: "cmd+backspace",
                windows: "ctrl+backspace",
                linux: "ctrl+backspace",
              },
              callback: (e: KeyboardEvent) => {
                const ae = document.activeElement as HTMLElement;
                const rowStr = ae?.getAttribute("data-row-index");
                if (rowStr == null) return;
                const rowIndex = parseInt(rowStr, 10);
                if (isNaN(rowIndex)) return;
                // Only delete data rows, not the add-row button
                if (rowIndex >= (localRowsRef.current?.length ?? 0)) return;
                const row = localRowsRef.current[rowIndex];
                if (!isRowDeletableFnRef.current(row, rowIndex)) return;
                e.preventDefault();
                const colStr = ae?.getAttribute("data-col-index");
                const colIndex = colStr != null ? parseInt(colStr, 10) : 0;
                deleteRowRef.current(rowIndex, {
                  row: rowIndex,
                  col: colIndex,
                });
              },
              scopeCheck: HotkeyScope.within,
            },
          ]
        : [],
    [isEditable],
  );

  const {
    id: navigationContextId,
    Scope,
    ref: scopeRef,
    node,
  } = useContextScope({ hotkeys: tableHotkeys });
  const { getCell, getCellProps } = useContextCellNavigation(
    navigationContextId,
    scopeRef,
    node,
    {
      rowCount,
      colCount,
      tab: true,
    },
  );
  const getClassName = useCallback(
    (objectKey: string) =>
      getContextClassnameForGrid(objectKey, columnContextId),
    [columnContextId],
  );

  // Debounced submit
  const debouncedSubmitRef = useRef(
    _.debounce(() => {
      if (localRowsRef.current) {
        onChangeField(localRowsRef.current);
        onSubmitField();
      }
    }, 1500),
  );

  // Keep ref in sync
  useEffect(() => {
    localRowsRef.current = localRows;
  }, [localRows]);

  // Sync with prop changes — skip while dragging/editing, while focus is
  // inside the table, or while localRows have diverged from the last
  // applied prop snapshot (covers remount focus flicker + debounce lag).
  const isInteractingRef = useRef(false);
  const lastAppliedPropRowsRef = useRef(propRows);
  const onInteractionStartRef = useRef(events?.onInteractionStart);
  const onInteractionEndRef = useRef(events?.onInteractionEnd);
  onInteractionStartRef.current = events?.onInteractionStart;
  onInteractionEndRef.current = events?.onInteractionEnd;

  useEffect(() => {
    if (pagination) {
      if (!_.isEqual(propRows, localRowsRef.current)) {
        lastAppliedPropRowsRef.current = propRows;
        setLocalRows((_.cloneDeep(propRows) ?? []).map(withRowMetadata));
        setRowIds((propRows ?? []).map(() => generateRowId()));
      }
      return;
    }

    // Read-only tables mirror props directly — local row metadata must not
    // block clearing rows when the parent value shrinks (e.g. triage journal).
    if (!isEditable) {
      if (_.isEqual(propRows, lastAppliedPropRowsRef.current)) {
        return;
      }
      lastAppliedPropRowsRef.current = propRows;
      setLocalRows((_.cloneDeep(propRows) ?? []).map(withRowMetadata));
      setRowIds((propRows ?? []).map(() => generateRowId()));
      return;
    }

    if (isMountedRef.current) {
      const focusInsideTable = Boolean(
        containerRef.current?.contains(document.activeElement),
      );
      if (_.isEqual(propRows, localRowsRef.current)) {
        lastAppliedPropRowsRef.current = propRows;
        return;
      }
      const localClean = _.isEqual(
        localRowsRef.current,
        lastAppliedPropRowsRef.current,
      );
      if (isInteractingRef.current || focusInsideTable) {
        return;
      }
      if (!localClean) {
        // The field changed underneath us while our own edit was still queued,
        // and the user is editing elsewhere (the same document can be edited
        // from the invoice canvas as well as this grid). Flushing the queued
        // snapshot would write rows we hold from before their change and delete
        // the ones we never saw, so drop it and take theirs.
        debouncedSubmitRef.current.cancel();
      }
      lastAppliedPropRowsRef.current = propRows;
      setLocalRows((_.cloneDeep(propRows) ?? []).map(withRowMetadata));
    } else {
      isMountedRef.current = true;
      lastAppliedPropRowsRef.current = propRows;
    }
  }, [propRows, withRowMetadata, pagination, generateRowId, isEditable]);

  const handleInteractionStart = useCallback(() => {
    if (isInteractingRef.current) return;
    isInteractingRef.current = true;
    onInteractionStartRef.current?.();
  }, []);

  const handleInteractionEnd = useCallback(() => {
    // Defer so cell remount / focus handoff can restore focus inside the
    // table before we drop the lock and allow prop sync / reconcile.
    queueMicrotask(() => {
      const focusInsideTable = Boolean(
        containerRef.current?.contains(document.activeElement),
      );
      if (focusInsideTable || !isInteractingRef.current) return;
      isInteractingRef.current = false;
      // Anything still queued goes up now. The debounce exists to coalesce
      // typing, not to hold an edit back once the user has left the table —
      // and a form's submit button reads the store on the very click that
      // blurs it, so a queued row would otherwise be saved without the last
      // thing typed into it.
      debouncedSubmitRef.current.flush();
      onInteractionEndRef.current?.();
    });
  }, []);

  // Table-owned interaction lock: survives ItemField/FieldCell remounts
  // that briefly clear document.activeElement.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isEditable) return;

    const onFocusIn = () => {
      handleInteractionStart();
    };
    const onFocusOut = (e: FocusEvent) => {
      if (el.contains(e.relatedTarget as Node | null)) return;
      handleInteractionEnd();
    };

    el.addEventListener("focusin", onFocusIn);
    el.addEventListener("focusout", onFocusOut);
    return () => {
      el.removeEventListener("focusin", onFocusIn);
      el.removeEventListener("focusout", onFocusOut);
    };
  }, [isEditable, handleInteractionStart, handleInteractionEnd]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedSubmitRef.current.cancel();
    };
  }, []);

  // Imperative handle
  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        const firstCell = getCell(0, 0);
        if (firstCell) {
          firstCell.focus();
          return;
        }
        // Cells may not be registered yet (e.g. AddItemRow mounting after
        // a React re-render triggered by shouldPassEvents). Retry once on
        // the next frame to give the commit phase time to run ref callbacks.
        requestAnimationFrame(() => {
          const retryCell = getCell(0, 0);
          if (retryCell) {
            retryCell.focus();
          } else {
            containerRef.current?.focus();
          }
        });
      },
      unsetFocus: () => {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && containerRef.current?.contains(activeElement)) {
          activeElement.blur();
        }
      },
    }),
    [localRows.length, columns.length, getCell],
  );

  // Handlers
  const handleCellChange = useCallback(
    (rowIndex: number, col: TableColumnType, cellValue: any) => {
      const row = localRowsRef.current[rowIndex];
      if (!row || !isRowMutableFnRef.current(row, rowIndex)) {
        return;
      }
      const nextRows = localRowsRef.current.map((prevRow, idx) => {
        if (idx !== rowIndex) return prevRow;
        const newRow = { ...prevRow };
        const preprocessOnChange = col?.preprocessOnChange;
        if (preprocessOnChange) {
          const processedRow = preprocessOnChange(col.objectKey, cellValue, {
            ...newRow,
          });
          processedRow[col.objectKey] = cellValue;
          return processedRow;
        }
        newRow[col.objectKey] = cellValue;
        return newRow;
      });
      // The ref is what the submit reads, and an effect syncs it a render too
      // late: anything that pushes rows upward before that effect runs — a
      // flush below, or the blur when the user clicks a form's submit button —
      // would send the row as it was *before* this edit.
      localRowsRef.current = nextRows;
      setLocalRows(nextRows);
      debouncedSubmitRef.current();
      // Choosing a reference is one discrete act, not typing: there is nothing
      // to coalesce, and the picker that made the choice sits outside the
      // table, so no blur follows to flush it either.
      if (col.type === "reference" || col.type === "references") {
        debouncedSubmitRef.current.flush();
      }
    },
    [],
  );

  const handleAddRow = useCallback(() => {
    const newRow: Record<string, any> = createRow?.() ?? {
      metadata: { _frontend_uuid: crypto.randomUUID() },
    };
    if (!newRow.metadata?._frontend_uuid) {
      newRow.metadata = {
        ...(newRow.metadata ?? {}),
        _frontend_uuid: crypto.randomUUID(),
      };
    }
    columns.forEach((col) => {
      if (!(col.objectKey in newRow)) {
        newRow[col.objectKey] = "";
      }
    });
    const newRowId = generateRowId();
    const nextRows = [...localRowsRef.current, newRow];
    localRowsRef.current = nextRows;
    setLocalRows(nextRows);
    setRowIds((prev) => [...prev, newRowId]);
    debouncedSubmitRef.current();

    // Focus the first field of the new row after render
    requestAnimationFrame(() => {
      const newRowIndex = localRowsRef.current.length - 1;
      const firstCell = getCell(newRowIndex, 0);
      if (firstCell) {
        firstCell.focus();
      }
    });
  }, [columns, createRow, generateRowId, getCell]);

  handleAddRowRef.current = handleAddRow;

  const handleDeleteRow = useCallback(
    (rowIndex: number, focusAfter?: { row: number; col: number }) => {
      const row = localRowsRef.current[rowIndex];
      if (!row || !isRowDeletableFnRef.current(row, rowIndex)) {
        return;
      }
      onDeleteRowRef.current?.(row, rowIndex);
      const prevRowCount = localRowsRef.current.length;
      const nextRows = localRowsRef.current.filter(
        (_, index) => index !== rowIndex,
      );
      localRowsRef.current = nextRows;
      setLocalRows(nextRows);
      setRowIds((prev) => prev.filter((_, index) => index !== rowIndex));
      setSelectedIndexes((prev) => prev.filter((index) => index !== rowIndex));
      debouncedSubmitRef.current();

      if (focusAfter) {
        requestAnimationFrame(() => {
          const newRowCount = prevRowCount - 1;
          if (newRowCount <= 0) {
            const addRow = getCell(0, 0);
            if (addRow) addRow.focus();
            else containerRef.current?.focus();
            return;
          }
          const targetRow = Math.min(focusAfter.row, newRowCount - 1);
          const cell = getCell(targetRow, focusAfter.col);
          if (cell) cell.focus();
          else containerRef.current?.focus();
        });
      }
    },
    [getCell],
  );

  // Keep ref in sync for the hotkey callback
  deleteRowRef.current = handleDeleteRow;

  const handleDeleteSelected = useCallback(() => {
    setLocalRows((prev) =>
      prev.filter((_, index) => !selectedIndexes.includes(index)),
    );
    setRowIds((prev) =>
      prev.filter((_, index) => !selectedIndexes.includes(index)),
    );
    setSelectedIndexes([]);
    debouncedSubmitRef.current();
  }, [selectedIndexes]);

  const handleToggleEditMode = useCallback(() => {
    setEditMode((prev) => (prev === "manual" ? "csv" : "manual"));
  }, []);

  const handleCsvSubmit = useCallback(
    (uploadedRows: Record<string, any>[]) => {
      setLocalRows(uploadedRows.map(withRowMetadata));
      setRowIds(uploadedRows.map(() => generateRowId()));
      setEditMode("manual");
      debouncedSubmitRef.current();
    },
    [generateRowId, withRowMetadata],
  );

  // Handle reorder
  const handleReorder = useCallback(
    (newRowIds: string[]) => {
      // Create a map of rowId -> row data for quick lookup
      const rowMap = new Map<string, Record<string, any>>();
      rowIds.forEach((id, index) => {
        rowMap.set(id, localRows[index]);
      });

      // Reorder rows based on new rowIds order
      const newRows = newRowIds.map((id) => rowMap.get(id)!).filter(Boolean);

      setRowIds(newRowIds);
      setLocalRows(newRows);
      debouncedSubmitRef.current();
    },
    [rowIds, localRows],
  );

  return (
    <Scope
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        // padding: isEditable ? "1px" : "0px",
        ...(fillHeight && { flex: 1, minHeight: 0, height: "100%" }),
        ["--field-table-cell-bg" as string]: gridColors.cellBackground,
        ["--field-table-header-bg" as string]: gridColors.headerBackground,
        ["--field-table-row-hover-bg" as string]: gridColors.rowHoverBackground,
        ...style,
      }}
    >
      {/* CSV Upload Mode */}
      {editMode === "csv" && (
        <CsvUploader
          columns={columns}
          onSubmit={handleCsvSubmit}
          onCancel={handleToggleEditMode}
        />
      )}

      {/* Manual Mode - Table with header and rows */}
      {editMode === "manual" && (
        <div
          className={cn(
            "isolate",
            fillHeight && "flex flex-col flex-1 min-h-0 h-full",
          )}
        >
          {/* Empty state - only show when not editable and no rows */}
          {localRows.length === 0 &&
            !isEditable &&
            !showHeaderWhenEmpty &&
            !summaryOnly && (
              <div className="pt-4 pb-4 text-center text-muted-foreground/80 font-semibold text-[11px] leading-snug">
                No items
              </div>
            )}

          {/* One scrollport for x+y so sticky left/right locked columns work. */}
          {(localRows.length > 0 || isEditable || showHeaderWhenEmpty) &&
            !summaryOnly && (
              <div
                className={cn(
                  "flex flex-col",
                  fillHeight && "flex-1",
                  fillHeight && "min-h-0",
                  "overflow-hidden",
                )}
              >
                <div
                  ref={scrollRef}
                  className={cn(
                    "overflow-auto max-w-full",
                    useOverlayScrollbarLayout &&
                      "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
                    showSummaryFill
                      ? "flex-1 min-h-0 flex flex-col"
                      : fillHeight
                        ? "flex-1 min-h-0"
                        : needsScroll && "max-h-(--max-height)",
                  )}
                  style={
                    {
                      "--max-height":
                        !showSummaryFill &&
                        !fillHeight &&
                        !fillHeight &&
                        needsScroll
                          ? MAX_VISIBLE_ROWS * rowHeight <= 1 &&
                            MAX_VISIBLE_ROWS * rowHeight !== 0
                            ? `${MAX_VISIBLE_ROWS * rowHeight * 100}%`
                            : `${MAX_VISIBLE_ROWS * rowHeight}px`
                          : undefined,
                    } as CSSProperties
                  }
                >
                  <div className="sticky top-0 z-6 bg-(--field-table-header-bg)">
                    <TableHeader
                      columns={columns}
                      orderedColumns={orderedColumns}
                      showActionsColumn={isEditable}
                      isEditable={isEditable}
                      isRowNumbered={isRowNumbered}
                      getClassName={getClassName}
                      resizeColumn={resizeColumn}
                      tableMinWidth={tableMinWidth}
                      actionsMenu={
                        isEditable ? (
                          <ActionsMenu
                            editMode={editMode}
                            hasRows={localRows.length > 0}
                            selectedCount={selectedIndexes.length}
                            onToggleEditMode={handleToggleEditMode}
                            onAddRow={handleAddRow}
                            onDeleteSelected={handleDeleteSelected}
                          />
                        ) : undefined
                      }
                    />
                  </div>

                  <table
                    className={cn(
                      fills ? "w-full" : "w-(--table-min-width-2)",
                      "min-w-(--table-min-width-2) border-collapse table-fixed",
                    )}
                    style={
                      {
                        "--table-min-width-2":
                          tableMinWidth <= 1 && tableMinWidth !== 0
                            ? `${tableMinWidth * 100}%`
                            : `${tableMinWidth}px`,
                      } as CSSProperties
                    }
                  >
                    <TableColgroup
                      orderedColumns={orderedColumns}
                      isEditable={isEditable}
                      isRowNumbered={isRowNumbered}
                      showActionsColumn={isEditable}
                      getClassName={getClassName}
                    />
                    {localRows.length > 0 &&
                    rowIds.length === localRows.length ? (
                      <Reorder.Group
                        axis="y"
                        values={rowIds}
                        onReorder={handleReorder}
                        as="tbody"
                        style={{
                          listStyle: "none",
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        {localRows.map((row, rowIndex) => {
                          const rowId = rowIds[rowIndex];
                          if (!rowId) return null;
                          return (
                            <ItemRow
                              key={rowId}
                              row={row}
                              rowIndex={rowIndex}
                              rowId={rowId}
                              orderedColumns={orderedColumns}
                              isEditable={isEditable}
                              rowMutable={isRowMutableFn(row, rowIndex)}
                              rowDeletable={isRowDeletableFn(row, rowIndex)}
                              onCellChange={handleCellChange}
                              onDeleteRow={handleDeleteRow}
                              getCellProps={getCellProps}
                              getClassName={getClassName}
                              deleteColIndex={deleteColIndex}
                              isLastRow={rowIndex === localRows.length - 1}
                              isRowNumbered={isRowNumbered}
                              outlineLastDataRow={
                                summaryRow != null && !isEditable
                              }
                              onInteractionStart={handleInteractionStart}
                              onInteractionEnd={handleInteractionEnd}
                            />
                          );
                        })}
                      </Reorder.Group>
                    ) : (
                      <tbody />
                    )}
                    {isEditable && (
                      <tbody>
                        <AddItemRow
                          onClick={handleAddRow}
                          getCellProps={getCellProps}
                          rowIndex={localRows.length}
                          orderedColumns={orderedColumns}
                          getClassName={getClassName}
                          hasActionsColumn={true}
                          stickyBottomOffset={summaryRow ? rowHeight : 0}
                          hasRowsAbove={localRows.length > 0}
                        />
                      </tbody>
                    )}
                    {summaryRow && (
                      <TableSummaryFooter
                        orderedColumns={orderedColumns}
                        getClassName={getClassName}
                        showActionsColumn={isEditable}
                        isRowNumbered={isRowNumbered}
                        summaryRow={summaryRow}
                        summaryColSpans={summaryColSpans}
                      />
                    )}
                  </table>
                  {/* The hatching under the rows, in the same scrollport: it
                      moves across with their columns, and pins what they pin. */}
                  {showSummaryFill && (
                    <div
                      data-testid="field-table-fill"
                      className="flex-1 min-h-6"
                    >
                      <TableSummaryFill
                        orderedColumns={orderedColumns}
                        getClassName={getClassName}
                        isEditable={isEditable}
                        isRowNumbered={isRowNumbered}
                        tableMinWidth={tableMinWidth}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      )}
    </Scope>
  );
}

// =============================================================================
// SKELETON
// =============================================================================

function FieldTableSkeleton({
  valueOptions,
  style,
  isEditable = false,
  overlayScrollbars = false,
  rowCount = MAX_VISIBLE_ROWS,
  columnContextId,
}: {
  valueOptions?: ReturnType<FieldDefinitionTableType["getValueOptions"]>;
  style?: React.CSSProperties;
  isEditable?: boolean;
  overlayScrollbars?: boolean;
  rowCount?: number;
  columnContextId: string;
}) {
  const gridColors = useResolvedGridAppearance();
  const { rowHeight, cellSize, widths, fills } = useFieldTableLayout();
  const containerRef = useRef<HTMLDivElement>(null);
  const columns = valueOptions?.columns || [];
  const orderedColumns = useMemo(() => getOrderedColumns(columns), [columns]);
  const fillHeight =
    style?.height === "100%" || style?.flex === 1 || style?.flex === "1";
  const skeletonRowCount = rowCount;
  const useOverlayScrollbarLayout = overlayScrollbars || fillHeight;
  const useCompactLayout = !fillHeight && !overlayScrollbars;
  const { scrollRef } = useFieldTableOverlayScrollbars(
    useOverlayScrollbarLayout,
  );

  const tableMinWidth = useMemo(() => {
    const allPercentageColumns =
      orderedColumns.length > 0 &&
      orderedColumns.every(({ col }) => col.widthMode === "percentage");
    if (allPercentageColumns && Object.keys(widths).length === 0) {
      return 0;
    }

    const dataMins = orderedColumns.reduce((sum, { col, category }) => {
      return sum + getColumnPixelWidth(col, category, widths);
    }, 0);
    const chrome =
      (isEditable ? DRAG_HANDLE_WIDTH : 0) +
      (isEditable ? ACTIONS_COLUMN_WIDTH : 0);
    return chrome + dataMins;
  }, [isEditable, orderedColumns, widths]);

  const getClassName = useCallback(
    (objectKey: string) =>
      getContextClassnameForGrid(objectKey, columnContextId),
    [columnContextId],
  );

  const bodyMaxHeight = fillHeight
    ? undefined
    : useCompactLayout
      ? undefined
      : (skeletonRowCount + 1) * rowHeight;

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex flex-col w-full isolate",
        fillHeight && "flex-1 min-h-0 h-full",
      )}
      // Its colours, and what it is given, over its own: as the table's.
      style={{
        ["--field-table-cell-bg" as string]: gridColors.cellBackground,
        ["--field-table-header-bg" as string]: gridColors.headerBackground,
        ["--field-table-row-hover-bg" as string]: gridColors.rowHoverBackground,
        ...style,
      }}
    >
      <div
        ref={useCompactLayout ? undefined : scrollRef}
        className={cn(
          useCompactLayout ? "overflow-hidden" : "overflow-auto",
          "max-w-full",
          !useCompactLayout &&
            useOverlayScrollbarLayout &&
            "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
          fillHeight
            ? "flex-1 min-h-0"
            : bodyMaxHeight != null &&
                (useCompactLayout ? "" : "max-h-(--max-height)"),
        )}
        style={
          {
            "--max-height":
              !fillHeight &&
              bodyMaxHeight != null &&
              !fillHeight &&
              !useCompactLayout
                ? (skeletonRowCount + 1) * rowHeight <= 1 &&
                  (skeletonRowCount + 1) * rowHeight !== 0
                  ? `${(skeletonRowCount + 1) * rowHeight * 100}%`
                  : `${(skeletonRowCount + 1) * rowHeight}px`
                : undefined,
          } as CSSProperties
        }
      >
        <div className="sticky top-0 z-6 bg-(--field-table-header-bg)">
          <table
            className={cn(
              fills ? "w-full" : "w-(--table-min-width-2)",
              "min-w-(--table-min-width-2) border-collapse table-fixed",
            )}
            style={
              {
                "--table-min-width-2":
                  tableMinWidth <= 1 && tableMinWidth !== 0
                    ? `${tableMinWidth * 100}%`
                    : `${tableMinWidth}px`,
              } as CSSProperties
            }
          >
            <TableColgroup
              orderedColumns={orderedColumns}
              isEditable={isEditable}
              showActionsColumn={isEditable}
              getClassName={getClassName}
            />
            <thead>
              <tr>
                {isEditable && (
                  <th
                    className={cn(
                      "p-[6px_8px] [border-bottom:1px_solid_var(--border)]",
                      cellSize === "md"
                        ? "font-medium text-sm leading-snug text-(--field-table-header-color,inherit) h-(--row-height-2) max-h-(--row-height-2) box-border align-middle"
                        : "font-medium text-xs leading-snug text-(--field-table-header-color,inherit) h-(--row-height-2) max-h-(--row-height-2) box-border align-middle",
                      "sticky top-0 z-5 bg-(--field-table-cell-bg) left-0 [box-shadow:inset_-1px_0_0_0_var(--border)] w-9 min-w-9 max-w-9 p-0",
                    )}
                    style={
                      {
                        "--row-height-2":
                          rowHeight <= 1 && rowHeight !== 0
                            ? `${rowHeight * 100}%`
                            : `${rowHeight}px`,
                      } as CSSProperties
                    }
                  />
                )}
                {orderedColumns.map(({ col }, colIdx) => {
                  const isLocked = col.isLockedLeft === true;
                  const isLastLocked = isLastLockedColumn(
                    orderedColumns,
                    colIdx,
                  );
                  const isLastUnlocked = colIdx === orderedColumns.length - 1;
                  return (
                    <th
                      key={`skel-head-${col.objectKey}`}
                      className={cn(
                        getClassName(col.objectKey),
                        "p-[6px_8px] [border-bottom:1px_solid_var(--border)]",
                        cellSize === "md"
                          ? "font-medium text-sm leading-snug text-(--field-table-header-color,inherit) h-(--row-height-2) max-h-(--row-height-2) box-border align-middle"
                          : "font-medium text-xs leading-snug text-(--field-table-header-color,inherit) h-(--row-height-2) max-h-(--row-height-2) box-border align-middle",
                        "sticky top-0 z-4 bg-(--field-table-header-bg) [text-align:var(--text-align)] overflow-hidden text-ellipsis whitespace-nowrap",
                        isLocked &&
                          "sticky left-(--left) z-5 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border)]",
                        isLastLocked &&
                          "[&::after]:[content:''] [&::after]:absolute [&::after]:top-0 [&::after]:bottom-0 [&::after]:left-full [&::after]:w-2 [&::after]:pointer-events-none [&::after]:[background:linear-gradient(to_right,_rgb(0_0_0_/_0.05),_transparent)] [&::after]:opacity-[0] [&::after]:[transition:opacity_0.2s_ease] [[data-scrolled-x]_&::after]:opacity-[1]",
                        isLastUnlocked || isLastLocked
                          ? "[border-right-width:initial] [border-right-style:none] border-r-[initial]"
                          : "[border-right-width:1px] [border-right-style:solid] border-r-border",
                      )}
                      style={
                        {
                          "--row-height-2":
                            rowHeight <= 1 && rowHeight !== 0
                              ? `${rowHeight * 100}%`
                              : `${rowHeight}px`,
                          "--text-align": col.style?.textAlign ?? "left",
                          "--left": isLocked
                            ? `${getLockedColumnLeftOffset(
                                orderedColumns,
                                colIdx,
                              )}px`
                            : undefined,
                        } as CSSProperties
                      }
                    >
                      {col.label}
                    </th>
                  );
                })}
                {isEditable && (
                  <th
                    className={cn(
                      "p-[6px_8px] [border-bottom:1px_solid_var(--border)]",
                      cellSize === "md"
                        ? "font-medium text-sm leading-snug text-(--field-table-header-color,inherit) h-(--row-height-2) max-h-(--row-height-2) box-border align-middle"
                        : "font-medium text-xs leading-snug text-(--field-table-header-color,inherit) h-(--row-height-2) max-h-(--row-height-2) box-border align-middle",
                      "sticky top-0 z-5 bg-(--field-table-cell-bg) right-0 [box-shadow:inset_1px_0_0_0_var(--border)] w-9 min-w-9 max-w-9 p-0",
                    )}
                    style={
                      {
                        "--row-height-2":
                          rowHeight <= 1 && rowHeight !== 0
                            ? `${rowHeight * 100}%`
                            : `${rowHeight}px`,
                      } as CSSProperties
                    }
                  />
                )}
              </tr>
            </thead>
          </table>
        </div>

        <table
          className={cn(
            fills ? "w-full" : "w-(--table-min-width-2)",
            "min-w-(--table-min-width-2) border-collapse table-fixed",
          )}
          style={
            {
              "--table-min-width-2":
                tableMinWidth <= 1 && tableMinWidth !== 0
                  ? `${tableMinWidth * 100}%`
                  : `${tableMinWidth}px`,
            } as CSSProperties
          }
        >
          <TableColgroup
            orderedColumns={orderedColumns}
            isEditable={isEditable}
            showActionsColumn={isEditable}
            getClassName={getClassName}
          />
          <tbody>
            {Array.from({ length: skeletonRowCount }).map((_, rowIndex) => {
              const isLastRow = rowIndex === skeletonRowCount - 1;
              return (
                <tr
                  key={`skel-row-${rowIndex}`}
                  // As a loaded row is: the table's colour under every cell,
                  // not only the locked column's.
                  className="bg-(--field-table-cell-bg)"
                >
                  {isEditable && (
                    <td
                      className={cn(
                        "p-0 sticky left-0 z-3 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border)] w-9 min-w-9 max-w-9",
                        isLastRow
                          ? "[border-bottom-width:initial] [border-bottom-style:none] border-b-[initial]"
                          : "[border-bottom-width:1px] [border-bottom-style:solid] border-b-border",
                      )}
                    />
                  )}
                  {orderedColumns.map(({ col }, colIdx) => {
                    const isLocked = col.isLockedLeft === true;
                    const isLastLocked = isLastLockedColumn(
                      orderedColumns,
                      colIdx,
                    );
                    const isLastCol = colIdx === orderedColumns.length - 1;
                    const isRightAligned = col.style?.textAlign === "right";
                    return (
                      <td
                        key={`skel-cell-${rowIndex}-${col.objectKey}`}
                        className={cn(
                          getClassName(col.objectKey),
                          "p-[6px_8px] h-(--row-height-2) box-border align-middle",
                          isLocked &&
                            "sticky left-(--left) z-2 bg-(--field-table-cell-bg) [box-shadow:inset_-1px_0_0_0_var(--border)]",
                          isLastRow
                            ? "[border-bottom-width:initial] [border-bottom-style:none] border-b-[initial]"
                            : "[border-bottom-width:1px] [border-bottom-style:solid] border-b-border",
                          isLastLocked &&
                            "[&::after]:[content:''] [&::after]:absolute [&::after]:top-0 [&::after]:bottom-0 [&::after]:left-full [&::after]:w-2 [&::after]:pointer-events-none [&::after]:[background:linear-gradient(to_right,_rgb(0_0_0_/_0.05),_transparent)] [&::after]:opacity-[0] [&::after]:[transition:opacity_0.2s_ease] [[data-scrolled-x]_&::after]:opacity-[1]",
                          isLastCol || isLastLocked
                            ? "[border-right-width:initial] [border-right-style:none] border-r-[initial]"
                            : "[border-right-width:1px] [border-right-style:solid] border-r-border",
                        )}
                        style={
                          {
                            "--row-height-2":
                              rowHeight <= 1 && rowHeight !== 0
                                ? `${rowHeight * 100}%`
                                : `${rowHeight}px`,
                            "--left": isLocked
                              ? `${getLockedColumnLeftOffset(
                                  orderedColumns,
                                  colIdx,
                                )}px`
                              : undefined,
                          } as CSSProperties
                        }
                      >
                        <div
                          className={cn(
                            "flex items-center w-full",
                            isRightAligned && "justify-end",
                          )}
                        >
                          <Skeleton
                            variant="text"
                            level={`body-${cellSize}`}
                            className={cn(
                              col.objectKey === "description"
                                ? "w-[85%]"
                                : "w-[65%]",
                              "max-w-full",
                            )}
                          />
                        </div>
                      </td>
                    );
                  })}
                  {isEditable && (
                    <td
                      className={cn(
                        "p-0 sticky right-0 z-3 bg-(--field-table-cell-bg) [box-shadow:inset_1px_0_0_0_var(--border)] w-9 min-w-9 max-w-9",
                        isLastRow
                          ? "[border-bottom-width:initial] [border-bottom-style:none] border-b-[initial]"
                          : "[border-bottom-width:1px] [border-bottom-style:solid] border-b-border",
                      )}
                    />
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function getColumnPixelWidth(
  col: TableColumnType,
  category: FieldCategory,
  widths: Record<string, number>,
): number {
  return (
    widths[col.objectKey] ??
    col.width ??
    col.minWidth ??
    (category === "numeric" ? 100 : 150)
  );
}

// =============================================================================
// EXPORT
// =============================================================================

export function FieldTable<T extends FieldDefinitionTableType<T>>({
  isLoading,
  valueOptions,
  pagination,
  rowHeight,
  cellSize = "sm",
  showHeaderWhenEmpty,
  summaryRow,
  summaryColSpans,
  summaryOnly = false,
  loadingRowCount,
  overlayScrollbars = false,
  isRowMutable,
  isRowDeletable,
  isRowNumbered,
  onDeleteRow,
  createRow,
  ref,
  ...props
}: FieldDefinitionFieldType<T> & {
  isLoading?: boolean;
  pagination?: FieldTablePaginationProps;
  rowHeight?: number;
  cellSize?: "sm" | "md";
  showHeaderWhenEmpty?: boolean;
  summaryRow?: Record<string, unknown>;
  summaryColSpans?: Record<string, number>;
  summaryOnly?: boolean;
  loadingRowCount?: number;
  overlayScrollbars?: boolean;
  isRowMutable?: (row: Record<string, any>, rowIndex: number) => boolean;
  isRowDeletable?: (row: Record<string, any>, rowIndex: number) => boolean;
  /**
   * Number each row (from 1) where its drag handle would be, for rows in an
   * order of their own that dragging would only misstate - a statement's lines.
   */
  isRowNumbered?: boolean;
  onDeleteRow?: (row: Record<string, any>, rowIndex: number) => void;
  createRow?: () => Record<string, any>;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const columnContextId = useId().replace(/:/g, "");

  // One set of widths for the table, loading or loaded: the skeleton draws the
  // widths its columns were dragged to, and they are still there when the rows
  // come back. Each in pixels: a column dragged keeps the width it was let go
  // at, whatever the panel's, and the columns after it move along - the table
  // scrolled across where they come to more than it is wide.
  const columns = valueOptions?.columns;
  const orderedColumns = useMemo(
    () => getOrderedColumns(columns ?? []),
    [columns],
  );
  const [widths, setWidths] = useState<Record<string, number>>({});
  const [isFlexDragged, setIsFlexDragged] = useState(false);
  // The others' widths before they last grew to fill what the one filling
  // left: what they give back as it is widened again.
  const grownFrom = useRef<Record<string, number> | null>(null);
  // The one that takes the room the others leave - the widest that holds
  // text, else the last. A table of shares has none: they fill it between
  // them, until one is dragged.
  const isShares =
    orderedColumns.length > 0 &&
    orderedColumns.every(({ col }) => col.widthMode === "percentage");
  const fillKey = isShares
    ? null
    : ((
        orderedColumns
          .filter(({ category }) => category === "text")
          .sort((a, b) => (b.col.width ?? 0) - (a.col.width ?? 0))[0] ??
        orderedColumns[orderedColumns.length - 1]
      )?.col.objectKey ?? null);
  // It fills at whatever width is left until it is dragged itself.
  const flexKey = isFlexDragged ? null : fillKey;
  const fills =
    flexKey != null || (isShares && Object.keys(widths).length === 0);
  const layout = useMemo(
    () => ({
      // ``md`` sits beside the main table, so its rows match that table's.
      rowHeight:
        rowHeight ??
        (cellSize === "md"
          ? Values.DEFAULT_ROW_HEIGHT
          : DEFAULT_FIELD_TABLE_ROW_HEIGHT),
      cellSize,
      widths,
      flexKey,
      fills,
    }),
    [rowHeight, cellSize, widths, flexKey, fills],
  );

  // A column's edge follows the pointer: the column takes what it moved, down
  // to the least it may be, and nothing else changes width. As the first drag
  // begins every column but the one still filling keeps the width it is drawn
  // at, read off its heading - so none jumps. The one filling, dragged, keeps
  // its own width too; where the columns then come to less than the table is
  // wide, the others grow in proportion to fill it, and where to more, they
  // keep theirs and the table is scrolled across. With none filling, no other
  // column is narrowed so far the table is narrower than its room.
  const resizeColumn = useCallback(
    (objectKey: string) => {
      const headingOf = (key: string) =>
        document.querySelector(
          `th.${getContextClassnameForGrid(key, columnContextId)}`,
        );
      const drawn = Object.fromEntries(
        orderedColumns.flatMap(({ col }) => {
          const heading = headingOf(col.objectKey);
          return heading &&
            (col.objectKey !== flexKey || col.objectKey === objectKey)
            ? [
                [
                  col.objectKey,
                  Math.round(heading.getBoundingClientRect().width),
                ],
              ]
            : [];
        }),
      );
      const column = orderedColumns.find(
        ({ col }) => col.objectKey === objectKey,
      );
      const table = headingOf(objectKey)?.closest("table");
      if (!column || drawn[objectKey] == null || !table?.parentElement) {
        return () => {};
      }
      const least = column.col.minWidth ?? RESIZED_COLUMN_MIN_WIDTH;
      const drawnWidth = Object.values(drawn).reduce((a, b) => a + b, 0);
      // What the columns have between them: the table's room, less the
      // gutters beside them.
      const room =
        table.parentElement.clientWidth -
        (table.getBoundingClientRect().width - drawnWidth);
      const widthBy = (offset: number) =>
        Math.max(least, Math.round(drawn[objectKey] + offset));
      setWidths((current) => ({ ...current, ...drawn }));
      if (objectKey !== fillKey) {
        grownFrom.current = null;
        // With none filling, it is not narrowed past what leaves the table
        // narrower than its room.
        const fewest =
          flexKey == null ? room - (drawnWidth - drawn[objectKey]) : 0;
        return (offset: number) =>
          setWidths((current) => ({
            ...current,
            [objectKey]: Math.max(fewest, widthBy(offset)),
          }));
      }
      setIsFlexDragged(true);
      const { [objectKey]: _, ...drawnOthers } = drawn;
      const others = grownFrom.current ?? drawnOthers;
      const othersWidth = Object.values(others).reduce((a, b) => a + b, 0);
      return (offset: number) => {
        const width = widthBy(offset);
        const scale = Math.max(1, (room - width) / othersWidth);
        grownFrom.current = scale > 1 ? others : null;
        const grown = Object.entries(others).map(
          ([key, ownWidth]) => [key, Math.round(ownWidth * scale)] as const,
        );
        // The last of them takes what rounding left over, so none is scrolled.
        const short =
          scale > 1
            ? room -
              width -
              grown.reduce((sum, [, grownWidth]) => sum + grownWidth, 0)
            : 0;
        setWidths((current) => ({
          ...current,
          ...Object.fromEntries(
            grown.map(([key, grownWidth], index) => [
              key,
              index === grown.length - 1 ? grownWidth + short : grownWidth,
            ]),
          ),
          [objectKey]: width,
        }));
      };
    },
    [orderedColumns, columnContextId, flexKey, fillKey],
  );

  return (
    <FieldTableLayoutContext.Provider value={layout}>
      {isLoading ? (
        <FieldTableSkeleton
          valueOptions={valueOptions}
          style={props.style}
          isEditable={Boolean(props.events?.onChangeField)}
          overlayScrollbars={overlayScrollbars}
          rowCount={loadingRowCount}
          columnContextId={columnContextId}
        />
      ) : (
        <FieldTableValue
          {...props}
          valueOptions={valueOptions}
          pagination={pagination}
          showHeaderWhenEmpty={showHeaderWhenEmpty}
          summaryRow={summaryRow}
          summaryColSpans={summaryColSpans}
          summaryOnly={summaryOnly}
          overlayScrollbars={overlayScrollbars}
          columnContextId={columnContextId}
          resizeColumn={resizeColumn}
          isRowMutable={isRowMutable}
          isRowDeletable={isRowDeletable}
          isRowNumbered={isRowNumbered}
          onDeleteRow={onDeleteRow}
          createRow={createRow}
          ref={ref}
        />
      )}
    </FieldTableLayoutContext.Provider>
  );
}
